import type { OpaqueVideoData, RawSceneData, RawVideoData } from './types';
import { Config } from './config';
import { Migrator } from './migrations/migrator';

type SliceVideoDataOptions = {
    /**
     * The scene index at which to start slicing
     */
    start: number;
    /**
     * The last scene index which is included in the return range
     */
    end: number;
    /**
     * If slicing should preserve transitions for the desired scene range.
     * If true and the scene at "start" has a transition, this will include an additional scene
     * prepended to the sliced range. Similarly, for "true", if the last scene in the range has
     * an outgoing transition (meaning the first excluded scene has a transition specified),
     * then an additional scene at the end is also included.
     */
    preserveTransitions: boolean;
};

type SliceVideoDataResult = {
    videoData: OpaqueVideoData;
    prependedScene: boolean;
    appendedScene: boolean;
};

/**
 * Returns a shallow, migrated copy of the input video data with the slides array sliced by the
 * start and end indices.
 *
 * Ensures that the output is still valid video data (e.g. the first scene doesn't have a
 * transition specified).
 *
 * This will also migrate video data to the latest version
 */
export function sliceVideoData(
    videoData: RawVideoData | OpaqueVideoData,
    options: SliceVideoDataOptions,
): SliceVideoDataResult {
    let prependedScene = false;
    let appendedScene = false;
    let start = options.start;
    let end = options.end + 1;

    if (options.preserveTransitions) {
        // Note we need to migrate scenes with the video data, so we have to assume we'll need
        // the extra scenes and then can remove them if we don't need them. For example, scene
        // groups (9.160) was a migration that spanned video & scene level data.
        if (start > 0) {
            prependedScene = true;
            start -= 1;
        }
        if (end < (videoData as RawVideoData).slides.length) {
            appendedScene = true;
            end += 1;
        }
    }

    // only migrate the scenes we need as a performance improvement
    const migratedVideoData = Migrator.updateVideoData({
        ...(videoData as RawVideoData),
        slides: (videoData as RawVideoData).slides.slice(start, end),
    } as OpaqueVideoData);

    const slides = migratedVideoData.slides;
    if (options.preserveTransitions) {
        if (prependedScene && !hasTransition(slides[1])) {
            slides.shift();
            prependedScene = false;
        }
        if (appendedScene && !hasTransition(slides[slides.length - 1])) {
            slides.pop();
            appendedScene = false;
        }
    }

    // update scene groups, so we only have scene groups that contain scenes
    const usedSceneGroups = new Set(migratedVideoData.slides.map((slide) => slide.sceneGroupId));
    const sceneGroups = migratedVideoData.sceneGroups as { id: string }[];
    migratedVideoData.sceneGroups = sceneGroups.filter((sceneGroup) =>
        usedSceneGroups.has(sceneGroup.id),
    );

    // If the branding scene was sliced away, turn off branding for this video data
    // so that we don't automatically add it when loading the data.
    const brandingScene = slides.find((scene) => scene.sceneCategory === Config.CATEGORY_BRAND);

    if (!brandingScene) {
        // Note that we don't mutate the original capabilities, instead, we create a new object.
        // This avoid unintended effects from changing the object that was passed to this function.
        migratedVideoData.capabilities = {
            ...(migratedVideoData.capabilities as Record<string, unknown>),
            noBranding: true,
        };
    }

    return {
        videoData: migratedVideoData,
        prependedScene,
        appendedScene,
    };
}

function hasTransition(scene: RawSceneData) {
    return scene.customTransition!.transitionUrl !== Config.NONE_TRANSITION_URL;
}
